import Image from 'next/image'
import { i18n, useTranslation } from 'next-i18next'
import { useRef, useCallback } from 'react'

import { Tooltip } from '@enuygun/design-system'

import SnapCarousel from 'components/SnapCarousel'

import * as S from './PopularAirlines.styled'
import { InfoCircleOutline } from '@enuygun/icons'
import { IconWrapper, TooltipText } from 'components/styled'

import { useAppState } from 'context/AppContext'
import { useDeviceType } from 'hooks/useDeviceType'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import sendTrackerLog from 'utils/tracker'

import { resolveApiHref } from 'utils/wingieApiLinks'

const marketTestIdPrefix = (market?: string) => (market === 'tr' ? 'enuygun' : 'wingie')

const sliceArray = (tempArray: any[], piece: number) => {
  const newArray: any[] = []
  for (let i = 0; i < tempArray.length; i += piece) {
    //@ts-ignore
    newArray.push(tempArray.slice(i, i + piece))
  }
  return newArray
}

const isRTL = (): boolean => {
  const lang = i18n?.language
  const dirLang = lang?.includes('-') ? lang.split('-')[1] : lang
  return i18n?.dir(dirLang) === 'rtl'
}

type AirlineCardProps = {
  image: string
  name: string
  url: string
  testIdKey: number
  order: number
  market?: string
  t: (key: string, options?: any) => string
  onClick: (name: string, url: string, image: string, order: number) => void
  showImageTestId?: boolean
}

const AirlineCard = ({ image, name, url, testIdKey, order, market, t, onClick, showImageTestId = false }: AirlineCardProps) => {
  const prefix = marketTestIdPrefix(market)
  return (
    <S.PopularAirlinesCard data-testid={`${prefix}-homepage-popular-airlines-cards-${testIdKey}`}>
      <a
        href={url}
        title={t('popularAirlinesImage', { name })}
        data-testid={`${prefix}-homepage-popular-airlines-cards-${testIdKey}-link`}
        onClick={() => onClick(name, url, image, order)}
      >
        <Image
          unoptimized
          loading="lazy"
          src={image}
          alt={t('popularAirlinesImage', { name })}
          fill
          {...(showImageTestId ? { 'data-testid': `${prefix}-homepage-popular-airlines-cards-${testIdKey}-image` } : {})}
        />
      </a>
    </S.PopularAirlinesCard>
  )
}

const PopularAirlines = ({ res }) => {
  const { t } = useTranslation('common')
  const { market, endpoint, headerLinks } = useAppState()
  const { isMobile } = useDeviceType()

  const rtl = isRTL()
  const prefix = marketTestIdPrefix(market)

  let isWingie = market !== 'tr'

  const seeAllAirlinesHref = resolveApiHref(isWingie ? headerLinks?.homePageLinks?.allAirlines : null, `${endpoint}${t('PopularAirlines.seeAllAirlines.link')}`)

  let data = res?.[3]?.popularAirlines

  const popularAirlinesContainerRef = useRef<HTMLDivElement>(null)

  // PopularAirlines card click tracking
  const handleAirlineClick = useCallback(
    (name: string, url: string, image: string, order: number) => {
      const environment = isMobile ? 'MOBILE_WEB' : 'DESKTOP_WEB'
      const requestId = getAnalyticsRequestId()

      sendTrackerLog({
        category: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
        page: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
        type: 'click',
        message: 'popular_airlines_card_click',
        environment: environment,
        requestId,
        airlineName: name,
        linkUrl: url,
        airlineImageUrl: image,
        cardOrder: order,
        isMobile: !!isMobile,
        market: market,
      })
    },
    [isMobile, market, isWingie]
  )

  // See all airlines click tracking
  const handleSeeAllClick = useCallback(() => {
    const seeAllAirlinesUrl = seeAllAirlinesHref
    const environment = isMobile ? 'MOBILE_WEB' : 'DESKTOP_WEB'
    const requestId = getAnalyticsRequestId()

    sendTrackerLog({
      category: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
      page: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
      type: 'click',
      message: 'see_all_airlines_click',
      environment: environment,
      requestId,
      linkUrl: seeAllAirlinesUrl,
      isMobile: !!isMobile,
      market: market,
    })
  }, [isMobile, market, isWingie, seeAllAirlinesHref])

  if (!data?.length) return null

  const sliced = sliceArray(data, 3)

  const tooltipText = t('PopularAirlines.headingTooltip', { defaultValue: false })

  const seeAllLink = (
    <S.SeeAllAirlines
      href={seeAllAirlinesHref}
      title={t('PopularAirlines.seeAllAirlines.text')}
      target="_blank"
      data-testid={`${prefix}-homepage-popular-airlines-heading-see-all-airlines`}
      onClick={handleSeeAllClick}
    >
      {t('PopularAirlines.seeAllAirlines.text')}
    </S.SeeAllAirlines>
  )

  return (
    <S.PopularAirlinesContainer ref={popularAirlinesContainerRef} data-testid={`${prefix}-homepage-popular-airlines-container`}>
      <S.PopularAirlinesHeading data-testid={`${prefix}-homepage-popular-airlines-heading`}>
        <div>
          {t('PopularAirlines.heading')}
          {tooltipText ? (
            <span style={{ display: 'inline-block' }} data-testid={`${prefix}-homepage-popular-airlines-heading-tooltip`}>
              <Tooltip
                bottomSheet={true}
                title={<div></div>}
                popover={{ width: '350px', popover: { placement: 'bottom' } }}
                button={
                  <IconWrapper bgColor={isMobile ? '#F8FAF9' : '#fff'} data-testid={`${prefix}-homepage-popular-airlines-heading-tooltip-button`}>
                    <InfoCircleOutline width={15.6} />
                  </IconWrapper>
                }
              >
                <TooltipText data-testid={`${prefix}-homepage-popular-airlines-heading-tooltip-text`}>{tooltipText}</TooltipText>
              </Tooltip>
            </span>
          ) : null}
        </div>
        {!isMobile && seeAllLink}
      </S.PopularAirlinesHeading>

      {isMobile ? (
        <div data-testid={`${prefix}-homepage-popular-airlines-cards`}>
          <SnapCarousel
            options={{
              perPage: 2.65,
              gap: 16,
            }}
            containerWidth={1120}
            isMobile={true}
            isRTL={rtl}
          >
            {sliced.map((group: Array<{ image: string; name: string; url: string }>, index) => (
              <div key={group.map((airline) => airline.url).join('-') || `group-${index}`}>
                {group.map(({ image, name, url }, key) => (
                  <AirlineCard key={url} image={image} name={name} url={url} testIdKey={key} order={index * 3 + key + 1} market={market} t={t} onClick={handleAirlineClick} />
                ))}
              </div>
            ))}
          </SnapCarousel>
          {seeAllLink}
        </div>
      ) : (
        <S.PopularAirlinesCards data-testid={`${prefix}-homepage-popular-airlines-cards`}>
          {data?.map(({ image, name, url }, key) => (
            <AirlineCard key={url} image={image} name={name} url={url} testIdKey={key} order={key + 1} market={market} t={t} onClick={handleAirlineClick} showImageTestId />
          ))}
        </S.PopularAirlinesCards>
      )}
    </S.PopularAirlinesContainer>
  )
}

export default PopularAirlines
